import {
  formatMoney,
  fromCents,
  type ScheduleBlock,
  type ScheduleMark,
  type ScheduleRow,
  type ScheduleSourceKind,
  toCents,
} from '@k-management/shared';
import { formatDate } from '../../lib/format.js';

export type ScheduleSide = 'in' | 'out' | 'all';

export type ScheduleFilters = {
  side: ScheduleSide;
  horizon: number;
  account: string;
  kind: ScheduleSourceKind | 'all';
  noIntercompany: boolean;
  search: string;
};

export const SCHEDULE_DEFAULT_FILTERS: ScheduleFilters = {
  side: 'all',
  horizon: 90,
  account: '',
  kind: 'all',
  noIntercompany: false,
  search: '',
};

export const BLOCK_ORDER: ScheduleBlock[] = [
  'da_riprogrammare',
  'inderogabili',
  'ordinari',
  'all_erogazione',
];

export const BLOCK_LABELS: Record<ScheduleBlock, string> = {
  da_riprogrammare: 'Da riprogrammare',
  inderogabili: 'Inderogabili — stipendi, F24, mutui, rateazioni',
  ordinari: 'Fornitori e clienti',
  all_erogazione: 'All’erogazione — senza data, fuori dal saldo finché non si dà il via libera',
};

export const KIND_LABELS: Record<ScheduleSourceKind, string> = {
  fattura: 'Fatture',
  proforma: 'Pro-forma',
  finanziamento: 'Finanziamenti',
  rateazione: 'Rateazioni',
  f24: 'F24',
  rata_commessa: 'Rate di commessa',
  costo_commessa: 'Costi di commessa',
  giroconto: 'Giroconti programmati',
};

export const MARK_LABELS: Record<ScheduleMark, string> = {
  anticipata: 'anticipata',
  intergruppo: 'intergruppo',
  nota_credito: 'NC',
  contrattualizzata: 'contrattualizzata',
  all_erogazione: 'all’erogazione',
  programmata: 'programmato a mano',
  stimato: 'stimato',
};

/** The balance only means something with both sides and every row of the account in view. */
export function showBalance(filters: ScheduleFilters, sorted: boolean): boolean {
  return (
    filters.side === 'all' &&
    filters.kind === 'all' &&
    !filters.noIntercompany &&
    filters.search.trim() === '' &&
    !sorted
  );
}

/** The rows in view. The planned transfers (giroconto) only show with both sides. */
export function filterRows(rows: ScheduleRow[], filters: ScheduleFilters): ScheduleRow[] {
  const search = filters.search.trim().toLowerCase();
  return rows.filter((row) => {
    if (filters.side !== 'all') {
      if (row.sourceKind === 'giroconto') return false;
      const cents = toCents(row.amount);
      if (filters.side === 'in' ? cents <= 0 : cents >= 0) return false;
    }
    if (filters.account && row.accountCode !== filters.account) return false;
    if (filters.kind !== 'all' && row.sourceKind !== filters.kind) return false;
    if (filters.noIntercompany && row.marks.includes('intergruppo')) return false;
    if (search && !`${row.subjectName} ${row.document}`.toLowerCase().includes(search)) {
      return false;
    }
    return true;
  });
}

/** Rows grouped by block, in the order of the page; empty blocks left out. */
export function groupByBlock(rows: ScheduleRow[]): { block: ScheduleBlock; rows: ScheduleRow[] }[] {
  return BLOCK_ORDER.map((block) => ({
    block,
    rows: rows.filter((row) => row.block === block),
  })).filter((group) => group.rows.length > 0);
}

/** A row of a planned transfer is changed through the transfer, not as an override. */
export function isTransferRow(row: Pick<ScheduleRow, 'sourceKind'>): boolean {
  return row.sourceKind === 'giroconto';
}

function csvCell(value: string): string {
  return /[";\r\n]/.test(value) ? `"${value.replace(/"/g, '""')}"` : value;
}

/** An amount as a plain Italian number: «1.234,50», no currency sign. */
function plainAmount(cents: number): string {
  return formatMoney(fromCents(cents)).replace(/\s*€/, '');
}

/** The view as a CSV for Excel in Italian (semicolon, decimal comma, header row). */
export function scheduleCsv(rows: ScheduleRow[], withBalance: boolean): string {
  const head = [
    'Blocco',
    'Anagrafica',
    'Documento',
    'Conto',
    'Scadenza',
    'Riprogrammata',
    'Ritardo (gg)',
    'Incasso',
    'Pagamento',
    ...(withBalance ? ['Saldo previsto'] : []),
    'Segni',
    'Nota',
  ];
  const lines = rows.map((row) => {
    const cents = toCents(row.amount);
    return [
      BLOCK_LABELS[row.block].split(' — ')[0]!,
      row.subjectName,
      row.document,
      row.accountCode ?? '',
      row.dueDate ? formatDate(row.dueDate) : '',
      row.rescheduleOn ? formatDate(row.rescheduleOn) : '',
      row.lateDays !== null && row.lateDays > 0 ? String(row.lateDays) : '',
      cents > 0 ? plainAmount(cents) : '',
      cents < 0 ? plainAmount(-cents) : '',
      ...(withBalance ? [row.balance === null ? '' : plainAmount(toCents(row.balance))] : []),
      row.marks.map((mark) => MARK_LABELS[mark]).join(', '),
      row.note ?? '',
    ].map(csvCell);
  });
  return [head.map(csvCell), ...lines].map((line) => line.join(';')).join('\r\n');
}
